import { toast } from "sonner";
import { LEGAL_DOCUMENTS, CONSENT_UI_VERSION, APPLICATION_FORM_VERSION } from "@/lib/legalDocuments";
import { CONSENT_PLAIN_TEXT, REQUIRED_CONSENT_KEYS } from "@/components/consent/ConsentControls";

export const TOTAL = 9;

export const STEP_TITLES = [
  "Advance Details", "Applicant Information", "Account Setup",
  "Company / Brokerage", "Subject Property", "Broker Contact",
  "Closing Officer / Title", "Upload Documents", "Consent & Submit",
];

export const initialState = {
  net_commission_amount: "", advance_amount_requested: "", expected_closing_date: "",
  under_contract: "", property_type: "",
  first_name: "", last_name: "", phone: "", email: "", state_licensed_in: "", license_number: "",
  account_email: "", password: "", confirm_password: "",
  brokerage_name: "", position: "", office_phone: "", brokerage_address: "",
  brokerage_city: "", brokerage_state: "", brokerage_zip: "",
  property_address: "", property_city: "", property_state: "", property_zip: "",
  property_closing_date: "", sale_price: "", expected_commission: "",
  broker_first_name: "", broker_last_name: "", broker_email: "", broker_phone: "",
  closing_firm_name: "", closing_officer_first_name: "", closing_officer_last_name: "",
  closing_officer_email: "", closing_officer_phone: "",
  uploaded_files: [],
  // 5 required consent checkboxes + 1 optional marketing consent.
  // All UNCHECKED by default — the spec requires an affirmative click.
  consent_terms: false,
  consent_privacy: false,
  consent_authorization: false,
  consent_esign: false,
  consent_communications: false,
  consent_marketing: false,
  source: "",
};

const MAX_ADVANCE_RATIO = 0.75;

export const passwordValid = (p) =>
  p.length >= 8 && /[A-Z]/.test(p) && /[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p);

const REQUIRED_BY_STEP = {
  2: ["first_name", "last_name", "phone", "email", "state_licensed_in", "license_number"],
  4: ["brokerage_name", "position", "office_phone", "brokerage_address", "brokerage_city", "brokerage_state", "brokerage_zip"],
  5: ["property_address", "property_city", "property_state", "property_zip", "property_closing_date", "sale_price", "expected_commission"],
  6: ["broker_first_name", "broker_last_name", "broker_email", "broker_phone"],
  7: ["closing_firm_name", "closing_officer_first_name", "closing_officer_last_name", "closing_officer_email", "closing_officer_phone"],
};

const fail = (msg) => { toast.error(msg); return false; };
const allFilled = (data, keys) => keys.every((k) => `${data[k]}`.trim() !== "");

function validateStep1(data) {
  const nc = parseFloat(data.net_commission_amount);
  const req = parseFloat(data.advance_amount_requested);
  if (!nc || !req || !data.expected_closing_date || !data.under_contract || !data.property_type) {
    return fail("Please complete all fields.");
  }
  if (req > nc * MAX_ADVANCE_RATIO) return fail("Advance cannot exceed 75% of net commission.");
  return true;
}

function validateStep3(data) {
  if (!data.account_email || !data.password || !data.confirm_password) return fail("Please complete all fields.");
  if (!passwordValid(data.password)) return fail("Password needs 8+ chars, 1 uppercase, 1 number, 1 symbol.");
  if (data.password !== data.confirm_password) return fail("Passwords must match.");
  return true;
}

function validateStep9(data) {
  const missing = REQUIRED_CONSENT_KEYS.filter((k) => !data[k]);
  if (missing.length) {
    return fail("Please review and accept each required consent before submitting.");
  }
  return true;
}

function validateSimpleStep(step, data) {
  const required = REQUIRED_BY_STEP[step];
  if (required && !allFilled(data, required)) return fail("Please complete all fields.");
  return true;
}

const VALIDATORS = { 1: validateStep1, 3: validateStep3, 9: validateStep9 };

export function validateStep(step, data) {
  const fn = VALIDATORS[step];
  return fn ? fn(data) : validateSimpleStep(step, data);
}

// Map each consent key to the legal document it references. This determines
// which document version is recorded on the audit trail per checkbox.
const CONSENT_TO_DOCUMENT = {
  consent_terms: LEGAL_DOCUMENTS.terms,
  consent_privacy: LEGAL_DOCUMENTS.privacy,
  consent_authorization: LEGAL_DOCUMENTS.authorization,
  consent_esign: LEGAL_DOCUMENTS.esign,
  consent_communications: LEGAL_DOCUMENTS.tcpa,
  consent_marketing: LEGAL_DOCUMENTS.tcpa,
};

/**
 * Build the payload for POST /api/consent-records. This is the append-only
 * audit trail. It captures exactly what wording was shown to the applicant,
 * which document versions were referenced, and the state of each checkbox.
 *
 * Only include the marketing consent if it was checked (recording an
 * unchecked optional consent is fine but takes up space — we include it too
 * for a full record).
 */
export function buildConsentPayload(data, { referring_page } = {}) {
  const nowUtc = new Date().toISOString();
  const localTs = new Date().toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "long",
  });
  const consents = Object.keys(CONSENT_TO_DOCUMENT).map((k) => {
    const doc = CONSENT_TO_DOCUMENT[k];
    return {
      key: k,
      checked: !!data[k],
      document_id: doc.id,
      document_version: doc.version,
      document_effective_date: doc.effectiveDate,
      wording_shown: CONSENT_PLAIN_TEXT[k],
    };
  });
  return {
    applicant_name: `${data.first_name} ${data.last_name}`.trim(),
    applicant_email: data.email,
    applicant_phone: data.phone || null,
    consents,
    consented_at_utc: nowUtc,
    consented_at_local: localTs,
    application_form_version: APPLICATION_FORM_VERSION,
    consent_ui_version: CONSENT_UI_VERSION,
    referring_page: referring_page || null,
    source: data.source ? `website application (src=${data.source})` : "website application",
  };
}

/**
 * Build the payload for POST /api/applications. Note that the server-side
 * ApplicationCreate model retains the legacy `certify_accurate`,
 * `authorize_verification`, and `consent_communications` fields for
 * backward compatibility — we set them to true here because the equivalent
 * (or more comprehensive) consents were recorded in the consent_record.
 */
export function buildPayload(data, { consent_record_id, consented_at_utc } = {}) {
  const legalVersions = Object.fromEntries(
    Object.entries(CONSENT_TO_DOCUMENT).map(([k, doc]) => [k, doc.version])
  );
  return {
    net_commission_amount: parseFloat(data.net_commission_amount),
    advance_amount_requested: parseFloat(data.advance_amount_requested),
    expected_closing_date: data.expected_closing_date,
    under_contract: data.under_contract === "yes",
    property_type: data.property_type,
    first_name: data.first_name, last_name: data.last_name,
    phone: data.phone, email: data.email,
    state_licensed_in: data.state_licensed_in, license_number: data.license_number,
    brokerage_name: data.brokerage_name, position: data.position, office_phone: data.office_phone,
    brokerage_address: data.brokerage_address, brokerage_city: data.brokerage_city,
    brokerage_state: data.brokerage_state, brokerage_zip: data.brokerage_zip,
    property_address: data.property_address, property_city: data.property_city,
    property_state: data.property_state, property_zip: data.property_zip,
    property_closing_date: data.property_closing_date,
    sale_price: parseFloat(data.sale_price),
    expected_commission: parseFloat(data.expected_commission),
    broker_first_name: data.broker_first_name, broker_last_name: data.broker_last_name,
    broker_email: data.broker_email, broker_phone: data.broker_phone,
    closing_firm_name: data.closing_firm_name,
    closing_officer_first_name: data.closing_officer_first_name,
    closing_officer_last_name: data.closing_officer_last_name,
    closing_officer_email: data.closing_officer_email,
    closing_officer_phone: data.closing_officer_phone,
    uploaded_file_paths: (data.uploaded_files || []).map((f) => f.storage_path),
    // Legacy consent fields kept as true — the append-only consent record is
    // the source of truth for what was actually shown & accepted.
    certify_accurate: true,
    authorize_verification: !!data.consent_authorization,
    consent_communications: !!data.consent_communications,
    consent_marketing: !!data.consent_marketing,
    consent_record_id: consent_record_id || null,
    legal_document_versions: legalVersions,
    consent_ui_version: CONSENT_UI_VERSION,
    application_form_version: APPLICATION_FORM_VERSION,
    consented_at_utc: consented_at_utc || null,
    ...(data.source ? { source: data.source } : {}),
  };
}
